import React from 'react'
import {
    Drawer,
    Box,
    Grid,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListItemButton,
    Typography,
    Chip,
} from '@mui/material'
import { Link } from 'react-router-dom'
import LoadingDefault from '../Elements/Loading/LoadingDefault'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes'
import { faTrashCan } from '@fortawesome/pro-duotone-svg-icons/faTrashCan'

import Pagination from '../Elements/Pagination'
import ItemCounter from '../Elements/ItemCounter'
import { Pager } from '../../models/Pager'

interface FavoriteItem {
    id: number | string
    content_dossier_id: number | string
    def_dossier_id: number | string
    summary: string
    created_at: string
}

interface FavoritesProps {
    kedo: any
    open: boolean
    handleSidebar: (newState: { isOpen: boolean }) => void
    closeSidebar: () => void
}

interface FavoritesState {
    items: FavoriteItem[]
    limit: number
    page: number
    pager: Pager
    search: string
    loading: boolean
    isDeleting: boolean
    loaded: boolean
    timeout?: NodeJS.Timeout
}

class Favorites extends React.Component<FavoritesProps, FavoritesState> {
    private searchRef: React.RefObject<HTMLInputElement>

    constructor(props: FavoritesProps) {
        super(props)

        this.searchRef = React.createRef<HTMLInputElement>()

        this.state = {
            items: [],
            limit: 10,
            page: 0,
            pager: new Pager(),
            search: '',
            loading: false,
            isDeleting: false,
            loaded: false,
        }

        this.fetchItems = this.fetchItems.bind(this)
        this.onChangePage = this.onChangePage.bind(this)
        this.onKeyUp = this.onKeyUp.bind(this)
        this.onChange = this.onChange.bind(this)
        this.resetSearch = this.resetSearch.bind(this)
        this.handleClose = this.handleClose.bind(this)
    }

    fetchItems(): void {
        const kedo = this.props.kedo
        if (this.state.loading) {
            return
        }
        const url = kedo.api().getContentDossierFavoriteEndpoint()
        this.setState({ loading: true, items: [] })

        const params = {
            params: {
                environment: kedo.env().getEnvironment().id,
                limit: this.state.limit,
                page: this.state.page,
            },
        }

        if (this.state.search) {
            params.params.search = this.state.search
        }

        kedo.api()
            .get(url, params)
            .then((response: any) =>
                this.setState({
                    loading: false,
                    loaded: true,
                    items: response.data.results,
                    pager: response.data.pager,
                })
            )
            .catch((error: any) =>
                this.setState({
                    loading: false,
                    loaded: true,
                })
            )
    }

    onChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ [event.target.name]: event.target.value } as Pick<
            FavoritesState,
            keyof FavoritesState
        >)
    }

    onKeyUp(): void {
        if (this.state.timeout) clearTimeout(this.state.timeout)
        const timeout = setTimeout(() => {
            this.fetchItems()
        }, 200)
        this.setState({ timeout })
    }

    shortenText(text: string): string {
        const dotsNeeded = text.length >= 50
        let trimmedString = text.substr(0, 50)

        trimmedString = trimmedString.substr(
            0,
            Math.min(trimmedString.length, trimmedString.lastIndexOf(' '))
        )
        trimmedString = dotsNeeded ? trimmedString + '...' : trimmedString

        return trimmedString
    }

    getDate(item: FavoriteItem): JSX.Element | undefined {
        if (!item.created_at) {
            return undefined
        }

        const date = new Date(item.created_at)
        const dateFormat = new Intl.DateTimeFormat('nl-NL', {
            dateStyle: 'medium',
            timeStyle: 'medium',
        })

        return (
            <Typography
                variant="caption"
                sx={{
                    fontSize: '9px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                }}
            >
                {dateFormat.format(date)}
            </Typography>
        )
    }

    getDefDossierName(
        item: FavoriteItem,
        defDossiers: any[],
        selectedLocale: any
    ): JSX.Element | undefined {
        if (!selectedLocale) {
            return undefined
        }

        const defDossier = defDossiers.find(
            (ddItem) => ddItem.id === item.def_dossier_id
        )
        if (
            !defDossier ||
            !defDossier.translations ||
            defDossier.translations.length <= 0
        ) {
            return undefined
        }

        const translation = defDossier.translations.find(
            (trItem: any) => trItem.culture === selectedLocale.code
        )

        return (
            <Link
                onClick={() => {
                    this.props.closeSidebar()
                }}
                to={`/defdossier/${item.def_dossier_id}`}
                style={{ textDecoration: 'none' }}
            >
                <Chip
                    size="small"
                    color="primary"
                    label={
                        translation && translation.name_plural
                            ? translation.name_plural
                            : item.def_dossier_id
                    }
                    clickable
                />
            </Link>
        )
    }

    deleteFavorite(data: FavoriteItem): void {
        const url = this.props.kedo
            .api()
            .getContentDossierFavoriteEndpoint(data.content_dossier_id)
        this.setState({ isDeleting: true })
        this.props.kedo
            .api()
            .delete(url)
            .then(() => this.setState({ isDeleting: false }, this.fetchItems))
            .catch((error) => {
                console.error('Delete error:', error)
                this.setState({
                    isDeleting: false,
                })
            })
    }

    renderItems(kedo: any): JSX.Element {
        let defDossiers: any[] = []
        let selectedLocale = null
        if (this.state.items.length > 0) {
            defDossiers = kedo.env().getCurrentEnvironmentDefDossiers()
            selectedLocale = kedo.env().getSelectedLocale()
        }

        if (this.state.loading) {
            return (
                <List sx={{ width: '100%' }}>
                    <ListItemButton>
                        <LoadingDefault
                            as={'span'}
                            size={'sm'}
                            message={kedo.t('Loading')}
                        />
                    </ListItemButton>
                </List>
            )
        }

        return (
            <>
                {this.state.items.length > 0 && (
                    <List sx={{ width: '100%', py: 0 }}>
                        {this.state.items.map((item) => (
                            <React.Fragment key={item.id}>
                                <ListItemButton
                                    component={Link}
                                    to={`/contentdossier/${item.content_dossier_id}`}
                                    onClick={() => {
                                        this.props.closeSidebar()
                                    }}
                                    sx={{
                                        position: 'relative',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        py: 1.5,
                                        px: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            mb: 0.5,
                                        }}
                                        className="quickSearchPills"
                                    >
                                        {this.getDefDossierName(
                                            item,
                                            defDossiers,
                                            selectedLocale
                                        )}
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {item.summary}
                                    </Typography>
                                    {this.getDate(item)}
                                    <IconButton
                                        className="trash-icon"
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                            this.deleteFavorite(item)
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            display: 'none',
                                            '.MuiListItemButton-root:hover &': {
                                                display: 'flex',
                                            },
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                    </IconButton>
                                </ListItemButton>
                            </React.Fragment>
                        ))}
                    </List>
                )}
                {this.state.items.length === 0 && !this.state.loading && (
                    <List sx={{ width: '100%' }}>
                        <ListItemButton sx={{ '& svg': { mr: 1 } }}>
                            <FontAwesomeIcon icon={faTimes} />{' '}
                            {kedo.t('No items found')}
                        </ListItemButton>
                    </List>
                )}
            </>
        )
    }

    onChangePage(page: number): void {
        this.setState({ page: page }, this.fetchItems)
    }

    renderFooter(): JSX.Element {
        return (
            <Grid container>
                <Grid size={12}>
                    <Pagination
                        pager={this.state.pager}
                        onChangePage={this.onChangePage}
                    />
                    <ItemCounter pager={this.state.pager} />
                </Grid>
            </Grid>
        )
    }

    componentDidUpdate(prevProps: FavoritesProps): void {
        if (this.props.open && !prevProps.open) {
            this.fetchItems()
        }
    }

    componentWillUnmount(): void {
        if (this.state.timeout) {
            clearTimeout(this.state.timeout)
        }
    }

    resetSearch(): void {
        this.setState(
            {
                search: '',
                items: [],
                page: 0,
            },
            this.fetchItems
        )
    }

    handleClose(): void {
        this.props.handleSidebar({ isOpen: false })
        this.resetSearch()
    }

    render() {
        const kedo = this.props.kedo

        return (
            <Drawer
                id="sidebar-favorite"
                anchor="right"
                open={this.props.open}
                onClose={this.handleClose}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: {
                            xs: '100%',
                            sm: '400px',
                        },
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    {/* Header */}
                    <Box
                        className="sidebar-header"
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 500,
                            bgcolor: '#dbdee6',
                            textAlign: 'center',
                            p: 2,
                            mb: 1.25,
                        }}
                    >
                        <Typography variant="h6">
                            {kedo.t('Favorites')}
                        </Typography>
                    </Box>

                    {/* Content - Scrollable */}
                    <Box
                        className="content"
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            px: 1.25,
                            pb: 1.25,
                        }}
                    >
                        <Grid container>
                            <Grid size={12} className="UpperSearchField">
                                <TextField
                                    fullWidth
                                    autoComplete="off"
                                    name="search"
                                    onChange={this.onChange}
                                    onKeyUp={this.onKeyUp}
                                    placeholder={kedo.t('Quick Search') + '...'}
                                    type="text"
                                    value={this.state.search}
                                    inputRef={this.searchRef}
                                    variant="outlined"
                                    size="small"
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        endAdornment:
                                            this.state.search.length > 0 ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        className="exitQuickSearch"
                                                        onClick={
                                                            this.resetSearch
                                                        }
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faTimes}
                                                        />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                    }}
                                />
                            </Grid>
                        </Grid>
                        {this.renderItems(kedo)}
                        {this.renderFooter()}
                    </Box>
                </Box>
            </Drawer>
        )
    }
}

export default Favorites
